/**
 * Wheel Themes Registry
 * Phase 7.0+: Premium theme system with detailed styling
 */

export type WheelThemeId =
  | "classic"
  | "modern"
  | "festive"
  | "minimal"
  | "beauty_rose_quartz_luxe";

export type WheelTheme = {
  id: WheelThemeId;
  name: string;

  // Canvas / stage
  stage: {
    transparentBackground: boolean; // IMPORTANT: no background
  };

  // Wheel geometry styling
  wheel: {
    rim: {
      width: number; // px relative (renderer interprets)
      outerGradient: [string, string, string]; // top->mid->bottom
      innerGradient: [string, string, string];
      shadow: { color: string; alpha: number; blur: number; y: number };
    };

    // 3D bevel illusion
    bevel: {
      enabled: boolean;
      highlight: { color: string; alpha: number };
      shade: { color: string; alpha: number };
      strength: number; // 0..1
    };

    // Gloss overlay arc
    gloss: {
      enabled: boolean;
      alpha: number; // 0..1
      gradient: [string, string]; // white -> transparent
      angleDeg: number; // position of gloss sweep
    };

    // Segment styling (if merchant doesn't pick a color, these are defaults)
    segments: {
      defaultPalette: string[]; // fallback colors
      stroke: { color: string; alpha: number; width: number };
      shadow: { color: string; alpha: number; blur: number; y: number };
      gradient: {
        enabled: boolean;
        lift: number; // brightness lift 0..1
        shade: number; // shade 0..1
      };
    };

    // Text (still editable per segment; this is only styling)
    text: {
      fontFamily: string;
      fontWeight: number | string;
      fill: string;
      shadow: { color: string; alpha: number; blur: number; y: number };
      stroke?: { color: string; alpha: number; width: number };
    };

    // Center button / cap (no hardcoded label!)
    center: {
      ring: {
        width: number;
        gradient: [string, string, string];
      };
      cap: {
        gradient: [string, string, string];
        shadow: { color: string; alpha: number; blur: number; y: number };
      };
    };
  };

  pointer: {
    style: "gem" | "classic";
    fillGradient: [string, string, string];
    stroke: { color: string; alpha: number; width: number };
    shadow: { color: string; alpha: number; blur: number; y: number };
  };

  motion: {
    spin: {
      direction: "cw"; // lock to clockwise
      minRotations: number;
      maxRotations: number;
      easing: "power4.out" | "expo.out" | "circ.out";
      // guard against "slow after first spin" bug:
      resetAngularVelocityBeforeSpin: boolean;
    };
  };
};

export const WHEEL_THEMES: Record<WheelThemeId, WheelTheme> = {
  classic: {
    id: "classic",
    name: "Classic",
    stage: { transparentBackground: true },
    wheel: {
      rim: {
        width: 22,
        outerGradient: ["#101225", "#1B1E3A", "#0B0D1A"],
        innerGradient: ["#EAEAEA", "#CFCFCF", "#FFFFFF"],
        shadow: { color: "#000000", alpha: 0.35, blur: 28, y: 10 },
      },
      bevel: {
        enabled: true,
        highlight: { color: "#FFFFFF", alpha: 0.18 },
        shade: { color: "#000000", alpha: 0.22 },
        strength: 0.6,
      },
      gloss: { enabled: true, alpha: 0.16, gradient: ["#FFFFFF", "rgba(255,255,255,0)"], angleDeg: -30 },
      segments: {
        defaultPalette: ["#FF6B6B", "#4ECDC4", "#FFE66D", "#A8E6CF"],
        stroke: { color: "#FFFFFF", alpha: 0.65, width: 2 },
        shadow: { color: "#000000", alpha: 0.14, blur: 10, y: 3 },
        gradient: { enabled: true, lift: 0.18, shade: 0.14 },
      },
      text: {
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto",
        fontWeight: 800,
        fill: "#FFFFFF",
        shadow: { color: "#000000", alpha: 0.28, blur: 8, y: 2 },
      },
      center: {
        ring: { width: 10, gradient: ["#FFFFFF", "#D9D9D9", "#FFFFFF"] },
        cap: { gradient: ["#1B1E3A", "#101225", "#2A2F5A"], shadow: { color: "#000000", alpha: 0.3, blur: 16, y: 6 } },
      },
    },
    pointer: {
      style: "classic",
      fillGradient: ["#FF4D5A", "#FF7581", "#FF2E3F"],
      stroke: { color: "#FFFFFF", alpha: 0.9, width: 3 },
      shadow: { color: "#000000", alpha: 0.3, blur: 16, y: 6 },
    },
    motion: { spin: { direction: "cw", minRotations: 5, maxRotations: 7, easing: "power4.out", resetAngularVelocityBeforeSpin: true } },
  },

  modern: {
    id: "modern",
    name: "Modern",
    stage: { transparentBackground: true },
    wheel: {
      rim: {
        width: 18,
        outerGradient: ["#FFFFFF", "#F2F3F7", "#FFFFFF"],
        innerGradient: ["#FFFFFF", "#EDEFF5", "#FFFFFF"],
        shadow: { color: "#000000", alpha: 0.18, blur: 26, y: 10 },
      },
      bevel: { enabled: true, highlight: { color: "#FFFFFF", alpha: 0.22 }, shade: { color: "#000000", alpha: 0.12 }, strength: 0.55 },
      gloss: { enabled: true, alpha: 0.12, gradient: ["#FFFFFF", "rgba(255,255,255,0)"], angleDeg: -20 },
      segments: {
        defaultPalette: ["#6C5CE7", "#00D2D3", "#FDCB6E", "#FF7675"],
        stroke: { color: "#FFFFFF", alpha: 0.9, width: 2 },
        shadow: { color: "#000000", alpha: 0.1, blur: 10, y: 3 },
        gradient: { enabled: true, lift: 0.16, shade: 0.1 },
      },
      text: {
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto",
        fontWeight: 800,
        fill: "#1A1D2A",
        shadow: { color: "#FFFFFF", alpha: 0.35, blur: 6, y: 1 },
        stroke: { color: "#FFFFFF", alpha: 0.55, width: 3 },
      },
      center: {
        ring: { width: 10, gradient: ["#FFFFFF", "#EDEFF5", "#FFFFFF"] },
        cap: { gradient: ["#111427", "#1B2240", "#0B0E1C"], shadow: { color: "#000000", alpha: 0.25, blur: 16, y: 6 } },
      },
    },
    pointer: {
      style: "classic",
      fillGradient: ["#111427", "#2C335A", "#0B0E1C"],
      stroke: { color: "#FFFFFF", alpha: 0.85, width: 3 },
      shadow: { color: "#000000", alpha: 0.22, blur: 14, y: 6 },
    },
    motion: { spin: { direction: "cw", minRotations: 5, maxRotations: 8, easing: "expo.out", resetAngularVelocityBeforeSpin: true } },
  },

  festive: {
    id: "festive",
    name: "Festive",
    stage: { transparentBackground: true },
    wheel: {
      rim: {
        width: 22,
        outerGradient: ["#FFF3E0", "#FFD7B5", "#FFF8F0"],
        innerGradient: ["#FFFFFF", "#FFE7D3", "#FFFFFF"],
        shadow: { color: "#000000", alpha: 0.22, blur: 28, y: 10 },
      },
      bevel: { enabled: true, highlight: { color: "#FFFFFF", alpha: 0.2 }, shade: { color: "#000000", alpha: 0.18 }, strength: 0.6 },
      gloss: { enabled: true, alpha: 0.18, gradient: ["#FFFFFF", "rgba(255,255,255,0)"], angleDeg: -35 },
      segments: {
        defaultPalette: ["#FF4D4D", "#00C48C", "#FFD166", "#845EC2"],
        stroke: { color: "#FFFFFF", alpha: 0.85, width: 2 },
        shadow: { color: "#000000", alpha: 0.12, blur: 10, y: 3 },
        gradient: { enabled: true, lift: 0.18, shade: 0.12 },
      },
      text: { fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto", fontWeight: 900, fill: "#FFFFFF", shadow: { color: "#000000", alpha: 0.3, blur: 8, y: 2 } },
      center: {
        ring: { width: 10, gradient: ["#FFFFFF", "#FFE7D3", "#FFFFFF"] },
        cap: { gradient: ["#C62828", "#FF5252", "#8E0000"], shadow: { color: "#000000", alpha: 0.28, blur: 16, y: 6 } },
      },
    },
    pointer: {
      style: "gem",
      fillGradient: ["#FFD166", "#FFF3B0", "#FFA000"],
      stroke: { color: "#FFFFFF", alpha: 0.9, width: 3 },
      shadow: { color: "#000000", alpha: 0.28, blur: 16, y: 6 },
    },
    motion: { spin: { direction: "cw", minRotations: 6, maxRotations: 9, easing: "power4.out", resetAngularVelocityBeforeSpin: true } },
  },

  minimal: {
    id: "minimal",
    name: "Minimal",
    stage: { transparentBackground: true },
    wheel: {
      rim: {
        width: 14,
        outerGradient: ["#FFFFFF", "#F6F7FA", "#FFFFFF"],
        innerGradient: ["#FFFFFF", "#F6F7FA", "#FFFFFF"],
        shadow: { color: "#000000", alpha: 0.12, blur: 22, y: 8 },
      },
      bevel: { enabled: true, highlight: { color: "#FFFFFF", alpha: 0.16 }, shade: { color: "#000000", alpha: 0.08 }, strength: 0.45 },
      gloss: { enabled: false, alpha: 0, gradient: ["#FFFFFF", "rgba(255,255,255,0)"], angleDeg: 0 },
      segments: {
        defaultPalette: ["#E9EEF5", "#DDE6F2", "#EEF2F8", "#D8E1EF"],
        stroke: { color: "#FFFFFF", alpha: 1, width: 2 },
        shadow: { color: "#000000", alpha: 0.06, blur: 8, y: 2 },
        gradient: { enabled: true, lift: 0.08, shade: 0.06 },
      },
      text: {
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto",
        fontWeight: 700,
        fill: "#1A1D2A",
        shadow: { color: "#FFFFFF", alpha: 0.25, blur: 4, y: 1 },
      },
      center: {
        ring: { width: 8, gradient: ["#FFFFFF", "#F0F2F7", "#FFFFFF"] },
        cap: { gradient: ["#FFFFFF", "#F6F7FA", "#FFFFFF"], shadow: { color: "#000000", alpha: 0.12, blur: 12, y: 4 } },
      },
    },
    pointer: {
      style: "classic",
      fillGradient: ["#111427", "#1B2240", "#0B0E1C"],
      stroke: { color: "#FFFFFF", alpha: 0.85, width: 3 },
      shadow: { color: "#000000", alpha: 0.18, blur: 14, y: 6 },
    },
    motion: { spin: { direction: "cw", minRotations: 4, maxRotations: 6, easing: "circ.out", resetAngularVelocityBeforeSpin: true } },
  },

  // ✅ NEW THEME: BEAUTY (ROSE QUARTZ LUXE)
  beauty_rose_quartz_luxe: {
    id: "beauty_rose_quartz_luxe",
    name: "Beauty (Rose Quartz Luxe)",
    stage: { transparentBackground: true }, // no background in playground either
    wheel: {
      rim: {
        width: 24,
        outerGradient: ["#FFF7FA", "#FAD3E2", "#FFF1F7"], // pearly rose
        innerGradient: ["#FFFFFF", "#F9E6EF", "#FFFFFF"],
        shadow: { color: "#000000", alpha: 0.22, blur: 32, y: 12 },
      },
      bevel: {
        enabled: true,
        highlight: { color: "#FFFFFF", alpha: 0.24 },
        shade: { color: "#7A2B4A", alpha: 0.14 },
        strength: 0.72,
      },
      gloss: {
        enabled: true,
        alpha: 0.22,
        gradient: ["rgba(255,255,255,0.95)", "rgba(255,255,255,0)"],
        angleDeg: -28,
      },
      segments: {
        defaultPalette: [
          "#FF6FAE", // lipstick
          "#F7C6D8", // blush
          "#B88CFF", // lavender highlight
          "#FFC2A6", // peach nude
          "#77E7D5", // mint accent
          "#FFE38A", // champagne gold
        ],
        stroke: { color: "#FFFFFF", alpha: 0.9, width: 2 },
        shadow: { color: "#000000", alpha: 0.1, blur: 10, y: 3 },
        gradient: { enabled: true, lift: 0.22, shade: 0.12 },
      },
      text: {
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto",
        fontWeight: 900,
        fill: "#FFFFFF",
        shadow: { color: "#3B0B1F", alpha: 0.22, blur: 8, y: 2 },
        stroke: { color: "rgba(0,0,0,0.10)", alpha: 0.35, width: 2 },
      },
      center: {
        ring: { width: 11, gradient: ["#FFF7FA", "#FAD3E2", "#FFF1F7"] },
        cap: {
          gradient: ["#1A1428", "#2A1B3D", "#0E0A16"], // deep luxe plum
          shadow: { color: "#000000", alpha: 0.28, blur: 18, y: 7 },
        },
      },
    },
    pointer: {
      style: "gem",
      fillGradient: ["#FF4D93", "#FF9AC6", "#C2185B"], // glossy gem pointer
      stroke: { color: "#FFFFFF", alpha: 0.95, width: 3 },
      shadow: { color: "#000000", alpha: 0.28, blur: 18, y: 7 },
    },
    motion: {
      spin: {
        direction: "cw",
        minRotations: 6,
        maxRotations: 9,
        easing: "expo.out",
        resetAngularVelocityBeforeSpin: true,
      },
    },
  },
};
